import React, { useEffect, useState } from "react";
import { datadogRum } from "@datadog/browser-rum";
import Dropdown from "../DropDown";
import { fetchWithCache } from "../../CacheDB/cached-Ref-Api";

interface StateData {
  state_code: string;
  state_name: string;
}

interface StateDropdownProps {
  selectedState: string;
  onStateChange: (
    selectedState: string,
    selectedStateObject?: StateData
  ) => void;
  disabled: boolean;
  className?: string;
  onFocus?: () => void;
}

const StateDropdown: React.FC<StateDropdownProps> = ({
  selectedState,
  onStateChange,
  disabled,
  className = "",
  onFocus,
}) => {
  const [stateData, setStateData] = useState<StateData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadData = async () => {
      const startTime = performance.now();
      try {
        const data = await fetchWithCache("states", "/ref-data/states");
        setStateData(data);

        datadogRum.addAction("Fetched States (Cached/Live)", {
          stateCount: data.length,
        });
      } catch (err) {
        setError("Failed to fetch state data");
        datadogRum.addError("State Data Fetch Failed", { error: err });
      } finally {
        setLoading(false);
        const endTime = performance.now();
        datadogRum.addAction("State Data Fetch Duration", {
          duration: endTime - startTime,
        });
      }
    };

    loadData();
  }, []);

  const options = stateData.map((state) => ({
    value: state.state_code,
    label: state.state_name,
  }));

  const handleChange = (value: string) => {
    const selected = stateData.find((item) => item.state_code === value);
    if (selected) {
      onStateChange(value, selected);
      datadogRum.addAction("State Selected", selected);
    }
  };

  return (
    <Dropdown
      options={options}
      selectedValue={selectedState}
      onValueChange={handleChange}
      disabled={disabled || loading || !!error}
      className={className}
      placeholder={loading ? "Loading States..." : error ?? "Select State"}
      onFocus={onFocus}
    />
  );
};

export default StateDropdown;
